/**
 * Global error logging
 *
 * Errors that nothing catches are otherwise only printed on the local console, which is no help at all when
 * the problem is on a user's machine: the remote log is all we get to see. A rejected promise in an event
 * handler is the worst of them - the handler simply stops, so a button appears to do nothing, and there is
 * no trace of why. Everything loose is logged here, so it reaches the remote log like any other error.
 *
 * Install it from main.ts, as early as possible:
 *
 *   installGlobalErrorLogging(app)
 */

import type { App } from 'vue'
import logger from '@/utilities/Logger'

/**
 * Describe whatever was thrown - it does not have to be an Error
 * @param reason The thrown value
 * @returns A description for the log
 */
function describe(reason: unknown): string {
	if (reason instanceof Error) { return `${reason.message}${reason.stack ? ` - ${reason.stack.split('\n')[1]?.trim()}` : ''}` }
	if (typeof reason === 'string') { return reason }

	try { return JSON.stringify(reason) }
	catch (_) { return String(reason) }
}

/**
 * Log every error and promise rejection that nothing else handles
 * @param app The Vue app to catch component errors from
 */
export default function installGlobalErrorLogging(app: App) {
	window.addEventListener('unhandledrejection', event => {
		logger.error(`Unhandled rejection: ${describe((event as PromiseRejectionEvent).reason)}`)
	})

	window.addEventListener('error', event => {
		logger.error(`Unhandled error: ${describe(event.error ?? event.message)}`)
	})

	app.config.errorHandler = (err, _instance, info) => {
		logger.error(`Unhandled error in ${info}: ${describe(err)}`)
	}
}
